import type { ReactNode } from "react";
import { ChangeEvent } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { IServicoOrcamento } from "interfaces/orcamento";
import { ServicoOrcamentoPayload } from "services/orcamentoServicos";
import {
  fieldSx,
  modalHeaderSx,
  modalPaperSx,
  ORCAMENTO_ACCENT,
  sectionCardSx,
} from "./orcamento-servicos.theme";

type Props = {
  open: boolean;
  editing: IServicoOrcamento | null;
  form: ServicoOrcamentoPayload;
  saving: boolean;
  onClose: () => void;
  onSave: () => void;
  onChange: (patch: Partial<ServicoOrcamentoPayload>) => void;
};

function SectionTitle({
  icon,
  title,
}: {
  icon: ReactNode;
  title: string;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" mb={2}>
      <Box sx={{ color: ORCAMENTO_ACCENT, display: "flex" }}>{icon}</Box>
      <MDTypography variant="button" fontWeight="bold">
        {title}
      </MDTypography>
    </Stack>
  );
}

export default function ServicoOrcamentoModal({
  open,
  editing,
  form,
  saving,
  onClose,
  onSave,
  onChange,
}: Props) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth PaperProps={{ sx: modalPaperSx }}>
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
              {editing ? "Edição" : "Cadastro"}
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {editing ? "Editar serviço" : "Novo serviço"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5, maxWidth: 520 }}>
              Itens do catálogo de orçamento exibidos na landing e usados na precificação interna.
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 0, overflow: "auto" }}>
        <Box p={3}>
          <Stack spacing={2.5}>
            <Box sx={sectionCardSx}>
              <SectionTitle icon={<Inventory2OutlinedIcon fontSize="small" />} title="Identificação" />
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nome do serviço"
                    placeholder="Ex.: Sinalização de percurso"
                    value={form.nome}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      onChange({ nome: e.target.value })
                    }
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Ordem de exibição"
                    value={String(form.ordem ?? 0)}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      onChange({ ordem: Number(e.target.value) || 0 })
                    }
                    inputProps={{ min: 0 }}
                    helperText="Menor número aparece primeiro"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={4}
                    label="Descrição (opcional)"
                    placeholder="Texto exibido na página de orçamento do site"
                    value={form.descricao ?? ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      onChange({ descricao: e.target.value })
                    }
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={sectionCardSx}>
              <SectionTitle icon={<PaymentsOutlinedIcon fontSize="small" />} title="Precificação" />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Preço unitário (R$)"
                    value={String(form.precoUnitario ?? 0)}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      onChange({ precoUnitario: Number(e.target.value) || 0 })
                    }
                    inputProps={{ min: 0, step: "0.01" }}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Unidade de medida"
                    placeholder="un, cone, hora, km..."
                    value={form.unidadeMedida ?? "un"}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      onChange({ unidadeMedida: e.target.value })
                    }
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.permiteQuantidade ?? true}
                        onChange={(e) => onChange({ permiteQuantidade: e.target.checked })}
                        color="warning"
                      />
                    }
                    label="Permite informar quantidade na solicitação de orçamento"
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={sectionCardSx}>
              <SectionTitle icon={<VisibilityOutlinedIcon fontSize="small" />} title="Visibilidade" />
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.exibirSite ?? true}
                        onChange={(e) => onChange({ exibirSite: e.target.checked })}
                        color="warning"
                      />
                    }
                    label="Exibir no site público"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.ativo ?? true}
                        onChange={(e) => onChange({ ativo: e.target.checked })}
                        color="warning"
                      />
                    }
                    label="Serviço ativo no sistema"
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "grey.200",
          bgcolor: "grey.50",
          justifyContent: "space-between",
        }}
      >
        <MDButton variant="text" color="dark" onClick={onClose} disabled={saving}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          onClick={onSave}
          disabled={saving}
          sx={{
            background: saving ? undefined : `linear-gradient(195deg, ${ORCAMENTO_ACCENT}, #e04a1a)`,
          }}
        >
          {saving ? "Salvando…" : editing ? "Salvar alterações" : "Criar serviço"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
